import React, { useEffect, useState, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Search, Bell, CheckCircle, Download, Calendar } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { StatusCard } from "../components/ui/StatusCard";
import api from "../lib/api";
import { useAuth } from "../lib/authContext";

interface NotificationRequest {
  id: string;
  created_at: string;
  type: string;
  name: string;
  issue: string;
  phone: string;
  message: string;
  product: string;
  user_id?: string;
  is_read?: boolean;
}

interface TabData {
  notifications: NotificationRequest[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  searchKeyword: string;
  limit: number;
  isLoading: boolean;
  typeFilter: string;
}

const Notifications: React.FC = () => {
  const { toast } = useToast();
  const { excelPermissions } = useAuth();
  const [activeTab, setActiveTab] = useState<string>("unread");
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());
  const [processingMarkAsRead, setProcessingMarkAsRead] = useState<string | null>(null);
  const [groupedTypes, setGroupedTypes] = useState<{ type: string; count: number }[]>([]);
  const [totalNotifications, setTotalNotifications] = useState<number>(0);

  // Unread tab data
  const [unreadData, setUnreadData] = useState<TabData>({
    notifications: [],
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    searchKeyword: "",
    limit: 10,
    isLoading: false,
    typeFilter: "",
  });

  // Read tab data
  const [readData, setReadData] = useState<TabData>({
    notifications: [],
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    searchKeyword: "",
    limit: 10,
    isLoading: false,
    typeFilter: "",
  });

  // Date filters (shared across both tabs)
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const fetchGroupedTypes = useCallback(async () => {
    try {
      const isRead = activeTab === "read";
      const searchKeyword =
        activeTab === "unread"
          ? unreadData.searchKeyword
          : readData.searchKeyword;

      const response = await api.getGroupedNotificationsByType({
        isRead,
        search: searchKeyword.trim() || undefined,
      });

      if (response.success) {
        setGroupedTypes(response.data);
        const total = response.data.reduce(
          (acc: number, curr: { count: number }) => acc + Number(curr.count),
          0
        );
        setTotalNotifications(total);
      }
    } catch (error) {
      console.error("Error fetching grouped types:", error);
    }
  }, [activeTab, unreadData.searchKeyword, readData.searchKeyword]);

  const fetchUnreadNotifications = useCallback(async () => {
    try {
      setUnreadData((prev) => ({ ...prev, isLoading: true }));

      // Use typeFilter as search if set, otherwise use searchKeyword
      const searchParam = unreadData.typeFilter || unreadData.searchKeyword.trim() || undefined;

      const response = await api.getNotificationRequests({
        page: unreadData.currentPage,
        limit: unreadData.limit,
        search: searchParam,
        isRead: false,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });

      if (response.success) {
        const totalPages = Math.ceil(response.total / unreadData.limit);
        setUnreadData((prev) => ({
          ...prev,
          notifications: response.data || [],
          totalPages: totalPages || 1,
          totalItems: response.total || 0,
          isLoading: false,
        }));
      } else {
        toast({
          title: "Error",
          description: "Failed to fetch unread notifications",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Failed to fetch unread notifications:", error);
      toast({
        title: "Error",
        description: "Failed to fetch unread notifications",
        variant: "destructive",
      });
    } finally {
      setUnreadData((prev) => ({ ...prev, isLoading: false }));
    }
  }, [
    unreadData.currentPage,
    unreadData.limit,
    unreadData.searchKeyword,
    unreadData.typeFilter,
    startDate,
    endDate,
    toast,
  ]);

  const fetchReadNotifications = useCallback(async () => {
    try {
      setReadData((prev) => ({ ...prev, isLoading: true }));

      // Use typeFilter as search if set, otherwise use searchKeyword
      const searchParam = readData.typeFilter || readData.searchKeyword.trim() || undefined;

      const response = await api.getNotificationRequests({
        page: readData.currentPage,
        limit: readData.limit,
        search: searchParam,
        isRead: true,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });

      if (response.success) {
        const totalPages = Math.ceil(response.total / readData.limit);
        setReadData((prev) => ({
          ...prev,
          notifications: response.data || [],
          totalPages: totalPages || 1,
          totalItems: response.total || 0,
          isLoading: false,
        }));
      } else {
        toast({
          title: "Error",
          description: "Failed to fetch read notifications",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Failed to fetch read notifications:", error);
      toast({
        title: "Error",
        description: "Failed to fetch read notifications",
        variant: "destructive",
      });
    } finally {
      setReadData((prev) => ({ ...prev, isLoading: false }));
    }
  }, [readData.currentPage, readData.limit, readData.searchKeyword, readData.typeFilter, startDate, endDate, toast]);

  // Fetch grouped types when tab or search changes
  useEffect(() => {
    fetchGroupedTypes();
  }, [fetchGroupedTypes]);

  // Fetch data on initial mount for both tabs
  useEffect(() => {
    fetchUnreadNotifications();
    fetchReadNotifications();
  }, []); // Empty dependency array for initial mount only

  // Fetch data when tab changes or filters change (after initial mount)
  useEffect(() => {
    if (activeTab === "unread") {
      fetchUnreadNotifications();
    } else if (activeTab === "read") {
      fetchReadNotifications();
    }
  }, [activeTab, fetchUnreadNotifications, fetchReadNotifications]);

  const handleMarkAsRead = async (id: string) => {
    try {
      setProcessingMarkAsRead(id);

      const response = await api.markNotificationAsRead(id);

      if (response.success) {
        toast({
          title: "Success",
          description: "Notification marked as read successfully",
        });
        // Refresh both tabs and grouped types
        fetchUnreadNotifications();
        fetchReadNotifications();
        fetchGroupedTypes();
      } else {
        toast({
          title: "Error",
          description: response.error || "Failed to mark notification as read",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
      toast({
        title: "Error",
        description: "Failed to mark notification as read",
        variant: "destructive",
      });
    } finally {
      setProcessingMarkAsRead(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const toggleMessageExpansion = (id: string) => {
    const newExpanded = new Set(expandedMessages);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedMessages(newExpanded);
  };

  const formatMessage = (message: string) => {
    const formattedMessage = message.replace(/Customer Message:/g, "Customer Message-");
    const lines = formattedMessage.split("\n").filter((line) => line.trim());
    return lines;
  };

  const truncateMessage = (message: string, id: string) => {
    const isExpanded = expandedMessages.has(id);
    const lines = formatMessage(message);
    const maxLines = 2;

    if (lines.length <= maxLines || isExpanded) {
      return (
        <>
          <div className="space-y-1">
            {lines.map((line, index) => (
              <div key={index} className="text-sm">
                {line.trim()}
              </div>
            ))}
          </div>
          {lines.length > maxLines && (
            <Button
              variant="link"
              size="sm"
              className="p-0 h-auto mt-2 text-blue-600"
              onClick={() => toggleMessageExpansion(id)}
            >
              Show Less
            </Button>
          )}
        </>
      );
    }

    return (
      <>
        <div className="space-y-1">
          {lines.slice(0, maxLines).map((line, index) => (
            <div key={index} className="text-sm">
              {line.trim()}
            </div>
          ))}
        </div>
        <Button
          variant="link"
          size="sm"
          className="p-0 h-auto mt-2 text-blue-600"
          onClick={() => toggleMessageExpansion(id)}
        >
          Show More
        </Button>
      </>
    );
  };

  const handleUnreadSearch = () => {
    setUnreadData((prev) => ({ ...prev, currentPage: 1 }));
    fetchUnreadNotifications();
    fetchGroupedTypes();
  };

  const handleReadSearch = () => {
    setReadData((prev) => ({ ...prev, currentPage: 1 }));
    fetchReadNotifications();
    fetchGroupedTypes();
  };

  const handleKeyPress = (
    e: React.KeyboardEvent,
    tab: "unread" | "read"
  ) => {
    if (e.key === "Enter") {
      if (tab === "unread") {
        handleUnreadSearch();
      } else {
        handleReadSearch();
      }
    }
  };

  const handleTypeFilterClick = (type: string) => {
    if (activeTab === "unread") {
      if (unreadData.typeFilter === type) {
        setUnreadData((prev) => ({ ...prev, typeFilter: "", currentPage: 1 }));
      } else {
        setUnreadData((prev) => ({ ...prev, typeFilter: type, currentPage: 1 }));
      }
    } else {
      if (readData.typeFilter === type) {
        setReadData((prev) => ({ ...prev, typeFilter: "", currentPage: 1 }));
      } else {
        setReadData((prev) => ({ ...prev, typeFilter: type, currentPage: 1 }));
      }
    }
  };

  const handleDownloadExcel = async (isRead: boolean) => {
    try {
      const searchKeyword = isRead ? readData.searchKeyword : unreadData.searchKeyword;
      const typeFilter = isRead ? readData.typeFilter : unreadData.typeFilter;
      const searchParam = typeFilter || searchKeyword.trim() || undefined;

      const response = await api.downloadNotificationRequestsExcel({
        isRead,
        search: searchParam,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });

      if (response.success && response.data) {
        // Convert base64 to blob
        const byteCharacters = atob(response.data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = response.filename || "notification_requests.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast({
          title: "Success",
          description: "Excel file downloaded successfully",
        });
      } else {
        toast({
          title: "Error",
          description: response.error || "Failed to download Excel file",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error downloading excel:", error);
      toast({
        title: "Error",
        description: "Error downloading Excel file",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="w-full p-6 bg-white dark:bg-gray-900">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold dark:text-white">
            Notification Requests
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage unread and read notification requests
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="unread" className="flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Unread ({unreadData.totalItems})
          </TabsTrigger>
          <TabsTrigger value="read" className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            Read ({readData.totalItems})
          </TabsTrigger>
        </TabsList>

        {/* Type Filter Cards */}
        <div className="grid grid-cols-4 gap-4 my-6">
          <StatusCard
            status="Total Notifications"
            count={totalNotifications}
            isSelected={false}
            onClick={() => {}}
          />
          {groupedTypes.map((item: { type: string; count: number }) => (
            <StatusCard
              key={item.type}
              status={item.type}
              count={item.count}
              isSelected={
                activeTab === "unread"
                  ? unreadData.typeFilter === item.type
                  : readData.typeFilter === item.type
              }
              onClick={() => handleTypeFilterClick(item.type)}
            />
          ))}
        </div>

        {/* Unread Tab */}
        <TabsContent value="unread" className="space-y-4">
          {/* Search Filter */}
          <div className="flex gap-2 items-center">
            <div className="relative flex-1 max-w-md">
              <Input
                placeholder="Search by name, issue, phone, product, or type..."
                value={unreadData.searchKeyword}
                onChange={(e) =>
                  setUnreadData((prev) => ({
                    ...prev,
                    searchKeyword: e.target.value,
                  }))
                }
                onKeyPress={(e) => handleKeyPress(e, "unread")}
                className="pr-10"
              />
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
            <Button
              onClick={handleUnreadSearch}
              disabled={unreadData.isLoading}
              className="px-6"
            >
              {unreadData.isLoading ? "Searching..." : "Search"}
            </Button>
            {excelPermissions.canDownloadExcel && (
              <Button
                onClick={() => handleDownloadExcel(false)}
                disabled={unreadData.isLoading}
                variant="outline"
                className="px-6"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Excel
              </Button>
            )}
          </div>

          {/* Date Range Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="pl-10"
                placeholder="Start Date"
              />
            </div>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="pl-10"
                placeholder="End Date"
              />
            </div>
          </div>

          {/* Pagination Size Selector */}
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Show:
              </span>
              <Select
                value={unreadData.limit.toString()}
                onValueChange={(value) =>
                  setUnreadData((prev) => ({
                    ...prev,
                    limit: Number(value),
                    currentPage: 1,
                  }))
                }
              >
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {unreadData.totalItems > 0 && (
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Showing{" "}
                {Math.min(
                  (unreadData.currentPage - 1) * unreadData.limit + 1,
                  unreadData.totalItems
                )}{" "}
                to{" "}
                {Math.min(
                  unreadData.currentPage * unreadData.limit,
                  unreadData.totalItems
                )}{" "}
                of {unreadData.totalItems} results
              </div>
            )}
          </div>

          {/* Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Issue</TableHead>
                  <TableHead>Phone No</TableHead>
                  <TableHead className="min-w-[300px]">Message</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Created At</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unreadData.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : unreadData.notifications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center">
                      No unread notification requests found
                    </TableCell>
                  </TableRow>
                ) : (
                  unreadData.notifications.map((notification) => (
                    <TableRow key={notification.id}>
                      <TableCell>{notification.name || "-"}</TableCell>
                      <TableCell>{notification.issue || "-"}</TableCell>
                      <TableCell>{notification.phone || "-"}</TableCell>
                      <TableCell className="min-w-[300px]">
                        {notification.message
                          ? truncateMessage(notification.message, notification.id)
                          : "-"}
                      </TableCell>
                      <TableCell>{notification.product || "-"}</TableCell>
                      <TableCell>
                        <span className="px-2 py-1 rounded-full text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                          {notification.type}
                        </span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {formatDate(notification.created_at)}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleMarkAsRead(notification.id)}
                          disabled={processingMarkAsRead === notification.id}
                          className="whitespace-nowrap min-w-[120px]"
                        >
                          {processingMarkAsRead === notification.id
                            ? "Marking..."
                            : "Mark as Read"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {unreadData.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Page {unreadData.currentPage} of {unreadData.totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setUnreadData((prev) => ({
                      ...prev,
                      currentPage: Math.max(1, prev.currentPage - 1),
                    }))
                  }
                  disabled={unreadData.currentPage === 1 || unreadData.isLoading}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setUnreadData((prev) => ({
                      ...prev,
                      currentPage: prev.currentPage + 1,
                    }))
                  }
                  disabled={
                    unreadData.currentPage >= unreadData.totalPages ||
                    unreadData.isLoading
                  }
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Read Tab */}
        <TabsContent value="read" className="space-y-4">
          {/* Search Filter */}
          <div className="flex gap-2 items-center">
            <div className="relative flex-1 max-w-md">
              <Input
                placeholder="Search by name, issue, phone, product, or type..."
                value={readData.searchKeyword}
                onChange={(e) =>
                  setReadData((prev) => ({
                    ...prev,
                    searchKeyword: e.target.value,
                  }))
                }
                onKeyPress={(e) => handleKeyPress(e, "read")}
                className="pr-10"
              />
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
            <Button
              onClick={handleReadSearch}
              disabled={readData.isLoading}
              className="px-6"
            >
              {readData.isLoading ? "Searching..." : "Search"}
            </Button>
            {excelPermissions.canDownloadExcel && (
              <Button
                onClick={() => handleDownloadExcel(true)}
                disabled={readData.isLoading}
                variant="outline"
                className="px-6"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Excel
              </Button>
            )}
          </div>

          {/* Date Range Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="pl-10"
                placeholder="Start Date"
              />
            </div>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="pl-10"
                placeholder="End Date"
              />
            </div>
          </div>

          {/* Pagination Size Selector */}
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Show:
              </span>
              <Select
                value={readData.limit.toString()}
                onValueChange={(value) =>
                  setReadData((prev) => ({
                    ...prev,
                    limit: Number(value),
                    currentPage: 1,
                  }))
                }
              >
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {readData.totalItems > 0 && (
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Showing{" "}
                {Math.min(
                  (readData.currentPage - 1) * readData.limit + 1,
                  readData.totalItems
                )}{" "}
                to{" "}
                {Math.min(
                  readData.currentPage * readData.limit,
                  readData.totalItems
                )}{" "}
                of {readData.totalItems} results
              </div>
            )}
          </div>

          {/* Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Issue</TableHead>
                  <TableHead>Phone No</TableHead>
                  <TableHead className="min-w-[300px]">Message</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Created At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {readData.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : readData.notifications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center">
                      No read notification requests found
                    </TableCell>
                  </TableRow>
                ) : (
                  readData.notifications.map((notification) => (
                    <TableRow key={notification.id}>
                      <TableCell>{notification.name || "-"}</TableCell>
                      <TableCell>{notification.issue || "-"}</TableCell>
                      <TableCell>{notification.phone || "-"}</TableCell>
                      <TableCell className="min-w-[300px]">
                        {notification.message
                          ? truncateMessage(notification.message, notification.id)
                          : "-"}
                      </TableCell>
                      <TableCell>{notification.product || "-"}</TableCell>
                      <TableCell>
                        <span className="px-2 py-1 rounded-full text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                          {notification.type}
                        </span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {formatDate(notification.created_at)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {readData.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Page {readData.currentPage} of {readData.totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setReadData((prev) => ({
                      ...prev,
                      currentPage: Math.max(1, prev.currentPage - 1),
                    }))
                  }
                  disabled={readData.currentPage === 1 || readData.isLoading}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setReadData((prev) => ({
                      ...prev,
                      currentPage: prev.currentPage + 1,
                    }))
                  }
                  disabled={
                    readData.currentPage >= readData.totalPages ||
                    readData.isLoading
                  }
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Notifications;
